import React, { useEffect, useRef, useState } from 'react';
import ListOfUser from './ListOfUser';
import ChatArea from './ChatArea';
import { closeConversation, fetchCustomer, getChatHistory } from '../../api/Chatbot';
import { useSocket } from '../../Contexts/SocketContext';

const Chatpage = () => {
  const { socketInstance } = useSocket();
  const userUUID = sessionStorage.getItem('userUUID');

  const [users, setUsers] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserHistory, setSelectedUserHistory] = useState<any>([]);
  const [selectedUser, setSelecteduser] = useState<any>();
  const [inputValue, setInputValue] = useState('');

  const usersRef = useRef(users);
  const selectedUserRef = useRef(selectedUser);
  const selectedUserHistoryRef = useRef(selectedUserHistory);

  const getAllCustomer = (defaultSelect: boolean) => {
    try {
      fetchCustomer(userUUID ? userUUID : '')
        .then((res: any) => {
          if (res?.payload) {
            const formattedUsers = res?.payload?.map((info: any) => ({
              id: info?.conversationUuid,
              customerUuid: info?.customerUuid,
              name: info?.customer?.fullName,
              lastMessage: info?.message?.message,
              date: info?.message?.createdAt,
              senderType: info?.message?.senderType,
              read: info?.message?.read,
              chatHistoryUuid: info?.message?.uuid,
              status: info?.status
            }));
            setUsers(formattedUsers);
            if (defaultSelect) {
              setSelecteduser(formattedUsers[0]);
              handleSelectedChat(formattedUsers[0]);
            }
            setLoading(false);
          } else {
            setLoading(false);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (e) {
      console.error(e);
    }
  };

  const handleSelectedChat = async (info: any) => {
    try {
      if (info) {
        setSelecteduser(info);
        const response = await getChatHistory(
          info?.id,
          info?.customerUuid,
          userUUID ? userUUID : ''
        );
        setSelectedUserHistory(response.payload.result);
        if (socketInstance && !info.read && info.senderType === 'System') {
          socketInstance.emit('mark-as-read', {
            customerUuid: info.customerUuid,
            userUuid: userUUID ? userUUID : ''
          });
        }
        if (users?.length > 0) {
          const updatedUserList = users.map((item: any) =>
            item.customerUuid === info.customerUuid
              ? {
                  ...item,
                  read: true
                }
              : item
          );
          setUsers(updatedUserList);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddMsg = () => {
    setSelectedUserHistory((prevHistory: any) => [
      ...prevHistory,
      {
        message: inputValue,
        senderType: 'Customer',
        createdAt: new Date().toISOString()
      }
    ]);
    setInputValue('');
    const updatedUserList = users.map((item: any) =>
      item.customerUuid === selectedUser.customerUuid
        ? {
            ...item,
            lastMessage: inputValue,
            senderType: 'Customer',
            date: new Date().toISOString(),
            read: true
          }
        : item
    );
    setUsers(updatedUserList);
  };

  const handleEndChat = async () => {
    const payload = { conversationUuid: selectedUser.id };
    const resp = await closeConversation(payload);
    if (resp.status === 201) {
      setSelecteduser({
        ...selectedUser,
        status: 'Close'
      });
      setSelectedUserHistory((prevHistory: any) => [
        ...prevHistory,
        {
          message: 'Thank you',
          senderType: 'Customer',
          createdAt: new Date().toISOString()
        }
      ]);
      getAllCustomer(false);
    }
  };

  const formatChatTime = (dateInput: any) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
      throw new Error('Invalid date provided');
    }

    const hours = date.getHours();
    const minutes = date.getMinutes();
    const isPM = hours >= 12;

    // Convert 24-hour format to 12-hour format
    const formattedHours = hours % 12 || 12; // '0' should be '12'
    const formattedMinutes = minutes.toString().padStart(2, '0'); // Add leading zero if needed

    // Return formatted time
    return `${formattedHours}:${formattedMinutes} ${isPM ? 'PM' : 'AM'}`;
  };

  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    selectedUserHistoryRef.current = selectedUserHistory;
  }, [selectedUserHistory]);

  useEffect(() => {
    getAllCustomer(true);
  }, []);

  useEffect(() => {
    if (socketInstance) {
      const handleUserToCustomerMessage = (response: any) => {
        const customerUuid = response?.customerUuid;
        if (customerUuid) {
          const isMatch = usersRef.current?.some((item: any) => item.customerUuid === customerUuid);
          if (isMatch) {
            const updatedUserList = usersRef.current?.reduce((result: any[], item: any) => {
              if (item.customerUuid === customerUuid) {
                return [
                  {
                    ...item,
                    lastMessage: response.message,
                    senderType: 'System',
                    read: selectedUserRef.current?.customerUuid === customerUuid ? true : false,
                    chatHistoryUuid: response.chatHistoryUuid,
                    date: new Date().toISOString(),
                    id: response.conversationUuid,
                    status: response.status
                  },
                  ...result
                ];
              } else {
                return [...result, item];
              }
            }, []);
            setUsers(updatedUserList);

            if (selectedUserRef.current?.customerUuid === customerUuid) {
              setSelectedUserHistory([
                ...selectedUserHistoryRef.current,
                {
                  message: response.message,
                  senderType: 'System',
                  createdAt: new Date().toISOString()
                }
              ]);

              setSelecteduser({
                ...selectedUserRef.current,
                id: response.conversationUuid,
                status: response.status
              });

              socketInstance.emit('mark-as-read', {
                customerUuid: customerUuid,
                userUuid: userUUID || ''
              });
            }
          } else {
            getAllCustomer(usersRef.current.length > 0 ? false : true);
          }
        }
      };

      socketInstance.on('customer-to-user-message', handleUserToCustomerMessage);
    }
  }, [socketInstance]);

  return (
    <div className="bg-[#1E1E1E] h-screen py-0 w-full px-2 overflow-y-auto xl:overflow-hidden relative">
      <div className="flex md:justify-between md:flex-row flex-col items-center mb-2.5 px-2">
        <label className="text-base text-white font-medium">Message</label>
      </div>

      {loading ? (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="loader border-t-4 border-[#006BEA] rounded-full w-12 h-12 animate-spin"></div>
        </div>
      ) : (
        <>
          {users.length === 0 ? (
            <div className="flex items-center justify-center h-[80%] absolute inset-0 text-gray-300 text-[14px] font-normal 
            select-none">
              No users found
            </div>
          ) : (
            <div className="bg-[#1E1E1E] w-full overflow-y-auto xl:overflow-hidden">
              <div className="flex flex-col xl:flex-row gap-2.5 pb-24 xl:h-[calc(100vh-150px)] xl:pb-0">
                <div className="w-full xl:w-[40%]">
                  <ListOfUser
                    users={users}
                    handleSelectedChat={handleSelectedChat}
                    selectedUser={selectedUser}
                    formatChatTime={formatChatTime}
                  />
                </div>

                <div className="w-full xl:w-[58.5%] bg-[#343434] rounded-lg mt-2">
                  {selectedUserHistory.length > 0 && (
                    <ChatArea
                      selectedUserHistory={selectedUserHistory}
                      selectedUser={selectedUser}
                      inputValue={inputValue}
                      setInputValue={setInputValue}
                      handleAddMsg={handleAddMsg}
                      formatChatTime={formatChatTime}
                      handleEndChat={handleEndChat}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Chatpage;
